import type {
    EsteiraFila, EsteiraGrupo, CobrancaAcaoStatus, CobrancaStatusNegociacao,
} from '../../../types';
import { FILAS, GRUPOS } from './filas.ts';

/**
 * Onde a pessoa estava no Painel de Cobrança — para, ao voltar, continuar
 * exatamente dali.
 *
 * Pedido do Financeiro (23/09/2026): quem trabalha a fila caso a caso vai até o
 * card 45 com "Carregar mais", abre o cliente, anota, sai para outra tela ou
 * outra janela — e ao voltar a lista estava no topo, cortada em 30 de novo.
 * Numa sequência de registros, isso é procurar o mesmo cliente toda vez.
 *
 * Guarda em sessionStorage, não na URL nem no banco: é conveniência da aba em
 * que a pessoa está trabalhando. Some ao fechar a aba, que é o comportamento
 * certo — amanhã é outra fila.
 */

export interface Filtros {
    grupo: EsteiraGrupo | '';
    fila: EsteiraFila | '';
    status: CobrancaAcaoStatus | '';
    statusNegociacao: CobrancaStatusNegociacao | '';
    responsavelId: string;
    search: string;
    minDiasParado: number;
    apenasSemDono: boolean;
    /** Só os clientes com retorno agendado (fora da lista até a data). */
    apenasAgendados: boolean;
    /** Só o que é do distrato (fora da lista padrão). */
    apenasDistrato: boolean;
    /** Só o que foi trabalhado hoje (#6211). */
    apenasTrabalhadasHoje: boolean;
    /** Atraso mais recente primeiro (padrão) ou o mais antigo. */
    ordem: 'recente' | 'antigo';
}

export const FILTROS_PADRAO: Filtros = {
    grupo: '', fila: '', status: 'pendente', statusNegociacao: '',
    responsavelId: '', search: '', minDiasParado: 0, apenasSemDono: false,
    apenasAgendados: false, apenasDistrato: false, apenasTrabalhadasHoje: false,
    ordem: 'recente',
};

/** Card que estava no alto da tela e a distância dele até o topo da janela. */
export interface AncoraPainel {
    id: string;
    topo: number;
}

export interface PosicaoPainel {
    usuarioId: string;
    salvoEm: number;
    filtros: Filtros;
    qtdVisivel: number;
    modo: string;
    scrollY: number;
    ancora: AncoraPainel | null;
}

const CHAVE = 'vigorhub:painel-cobranca:posicao';

/**
 * Um expediente. Passado disso a posição não volta: além de o lugar na fila já
 * não significar nada, restaurar o recorte de ontem pularia o "Meus itens"
 * que a tela aplica sozinha na primeira visita do dia.
 */
export const VALIDADE_MS = 8 * 60 * 60_000;

/** Altura do cabeçalho fixo do app (`pt-16` no `<main>`): o que está por baixo dele não conta como visível. */
const ALTURA_CABECALHO = 64;

interface Contexto {
    usuarioId: string;
    /** `?fila=` e `?grupo=` da URL — o link do sininho, ou o voltar do navegador. */
    filaDaUrl: string;
    grupoDaUrl: string;
    agora: number;
}

const ehNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Valida o que estava guardado. Qualquer coisa estranha descarta tudo em vez
 * de restaurar pela metade: abrir no topo é o comportamento antigo, restaurar
 * um recorte torto é pior que ele.
 *
 * Um link que aponta para OUTRA fila (o sininho levando para "mora sem envio")
 * vence a posição guardada: a pessoa clicou querendo ver aquilo. Se o link é
 * da mesma fila — o voltar do navegador devolve a URL com `?fila=` — restaura.
 */
export function interpretarPosicao(cru: string | null, ctx: Contexto): PosicaoPainel | null {
    if (!cru) return null;
    let p: any;
    try { p = JSON.parse(cru); } catch { return null; }
    if (!p || typeof p !== 'object' || !p.filtros || typeof p.filtros !== 'object') return null;

    if (p.usuarioId !== ctx.usuarioId) return null;
    if (!ehNumero(p.salvoEm) || ctx.agora - p.salvoEm > VALIDADE_MS || p.salvoEm > ctx.agora) return null;
    if (!ehNumero(p.qtdVisivel) || !Number.isInteger(p.qtdVisivel) || p.qtdVisivel < 1) return null;
    if (!ehNumero(p.scrollY) || p.scrollY < 0) return null;
    if (typeof p.modo !== 'string') return null;

    const filtros: Filtros = { ...FILTROS_PADRAO, ...p.filtros };
    // Fila ou grupo que deixou de existir entre um deploy e outro vira "todas".
    if (filtros.fila && !FILAS[filtros.fila as EsteiraFila]) filtros.fila = '';
    if (filtros.grupo && !GRUPOS.some(g => g.grupo === filtros.grupo)) filtros.grupo = '';
    if (filtros.ordem !== 'antigo') filtros.ordem = 'recente';

    if (ctx.filaDaUrl && filtros.fila !== ctx.filaDaUrl) return null;
    if (ctx.grupoDaUrl && filtros.grupo !== ctx.grupoDaUrl) return null;

    const ancora = p.ancora && typeof p.ancora.id === 'string' && ehNumero(p.ancora.topo)
        ? { id: p.ancora.id, topo: p.ancora.topo }
        : null;

    return {
        usuarioId: p.usuarioId,
        salvoEm: p.salvoEm,
        filtros,
        qtdVisivel: p.qtdVisivel,
        modo: p.modo,
        scrollY: p.scrollY,
        ancora,
    };
}

// sessionStorage pode lançar (aba anônima, dados do site bloqueados): nos dois
// casos a tela só volta ao comportamento antigo, de abrir no topo.

export function lerPosicao(ctx: Omit<Contexto, 'agora'>): PosicaoPainel | null {
    try {
        return interpretarPosicao(sessionStorage.getItem(CHAVE), { ...ctx, agora: Date.now() });
    } catch {
        return null;
    }
}

export function gravarPosicao(posicao: PosicaoPainel): void {
    try {
        sessionStorage.setItem(CHAVE, JSON.stringify(posicao));
    } catch { /* sem armazenamento, sem restauração */ }
}

/** O primeiro card que aparece abaixo do cabeçalho — é por ele que a tela se reposiciona. */
export function ancoraVisivel(lista: HTMLElement | null): AncoraPainel | null {
    if (!lista) return null;
    for (const el of lista.querySelectorAll<HTMLElement>('[data-demanda-id]')) {
        const { top, bottom } = el.getBoundingClientRect();
        if (bottom > ALTURA_CABECALHO) return { id: el.dataset.demandaId ?? '', topo: top };
    }
    return null;
}

/**
 * Reposiciona pelo card, não pelo pixel: se um colega resolveu demandas acima
 * enquanto a pessoa estava fora, a lista encolheu e o `scrollY` antigo cairia
 * em outro cliente. O pixel só vale quando o card de referência saiu da fila.
 */
export function voltarParaPosicao(lista: HTMLElement | null, posicao: PosicaoPainel): void {
    const { ancora } = posicao;
    const alvo = ancora
        ? lista?.querySelector<HTMLElement>(`[data-demanda-id="${CSS.escape(ancora.id)}"]`)
        : null;
    const topo = alvo && ancora
        ? window.scrollY + alvo.getBoundingClientRect().top - ancora.topo
        : posicao.scrollY;
    window.scrollTo(0, Math.max(0, topo));
}
